import type {
  PlaybackData,
  PlaybackNote,
  PlaybackTempo,
} from "@midi-fantom-utils/midi-util";
import { tickToSeconds, secondsToTick } from "./timeline.js";

export type ControllerStatus =
  | "idle"
  | "ready"
  | "playing"
  | "paused"
  | "ended"
  | "error";

export type ControllerState = {
  readonly status: ControllerStatus;
  readonly positionMs: number;
  readonly durationMs: number;
};

export type PlaybackController = {
  getState(): ControllerState;
  subscribe(listener: () => void): () => void;
  play(): void;
  pause(): void;
  stop(): void;
  seek(positionMs: number): void;
  setOnScheduleNote(cb: ((note: PlaybackNote, whenSec: number) => void) | null): void;
  setOnStopAll(cb: (() => void) | null): void;
  dispose(): void;
};

export type PlaybackControllerOptions = {
  audioCtx: AudioContext;
  getNotes: () => ReadonlyArray<PlaybackNote>;
  tempos: PlaybackTempo[];
  ppq: number;
  lookaheadSec?: number;
  onScheduleNote?: (note: PlaybackNote, whenSec: number) => void;
  onStopAll?: () => void;
};

const DEFAULT_LOOKAHEAD_SEC = 0.2;

const findFirstNoteAtOrAfter = (
  notes: ReadonlyArray<PlaybackNote>,
  tick: number
): number => {
  let lo = 0;
  let hi = notes.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (notes[mid]!.startTicks < tick) lo = mid + 1;
    else hi = mid;
  }
  return lo;
};

/**
 * Длительность плоского `PlaybackData` в миллисекундах.
 *
 * Общий helper — единая точка правды для MidiPlayer (UI до создания
 * AudioContext) и `createPlaybackController` (runtime duration).
 * Если сюда добавятся edge-cases (отрицательные startTicks, length-0
 * tempos и т.п.), оба потребителя увидят фикс одновременно.
 */
export const computePlaybackDurationMs = (data: PlaybackData): number => {
  return computeDurationMs(data.notes, data.tempos, data.ppq);
};

const computeDurationMs = (
  notes: ReadonlyArray<PlaybackNote>,
  tempos: PlaybackTempo[],
  ppq: number
): number => {
  if (notes.length === 0) return 0;
  let max = 0;
  for (const n of notes) {
    const end = n.startTicks + n.durationTicks;
    if (end > max) max = end;
  }
  return Math.round(tickToSeconds(max, tempos, ppq) * 1000);
};

type Transport = {
  startCtxTime: number;
  startTick: number;
};

const shallowEqualState = (a: ControllerState, b: ControllerState): boolean => {
  if (a.status !== b.status) return false;
  if (a.positionMs !== b.positionMs) return false;
  if (a.durationMs !== b.durationMs) return false;
  return true;
};

export const createPlaybackController = (
  opts: PlaybackControllerOptions
): PlaybackController => {
  const { audioCtx, getNotes, tempos, ppq } = opts;
  const lookaheadSec = opts.lookaheadSec ?? DEFAULT_LOOKAHEAD_SEC;

  const computeDuration = () => computeDurationMs(getNotes(), tempos, ppq);

  let positionMs = 0;
  let status: ControllerStatus = "idle";
  let durationMs = computeDuration();
  let cachedState: ControllerState = Object.freeze({
    status,
    positionMs,
    durationMs,
  });

  const transportRef: { current: Transport | null } = { current: null };
  const pausedAtRef: { current: { tick: number } | null } = { current: null };
  const nextNoteIndexRef: { current: number } = { current: 0 };
  const rafRef: { current: number | null } = { current: null };

  let onScheduleNote: ((note: PlaybackNote, whenSec: number) => void) | null =
    opts.onScheduleNote ?? null;
  let onStopAll: (() => void) | null = opts.onStopAll ?? null;

  const listeners = new Set<() => void>();
  let disposed = false;

  const setState = (
    nextStatus: ControllerStatus,
    nextPositionMs: number,
    nextDurationMs: number
  ) => {
    const next: ControllerState = { status: nextStatus, positionMs: nextPositionMs, durationMs: nextDurationMs };
    if (shallowEqualState(cachedState, next)) return;
    cachedState = Object.freeze(next);
    for (const l of listeners) l();
  };

  const refreshDuration = () => {
    durationMs = computeDuration();
  };

  const tickAt = (ctxTime: number): number => {
    const t = transportRef.current;
    if (!t) return 0;
    const elapsed = ctxTime - t.startCtxTime;
    if (elapsed <= 0) return t.startTick;
    return t.startTick + secondsToTick(elapsed, tempos, ppq);
  };

  const stopRaf = () => {
    if (rafRef.current !== null) {
      const caf =
        typeof cancelAnimationFrame === "function"
          ? cancelAnimationFrame
          : null;
      if (caf) caf(rafRef.current);
      else if (
        typeof window !== "undefined" &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = null;
    }
  };

  const requestRaf = (cb: (t: number) => void): void => {
    const raf =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame
        : null;
    if (raf) {
      rafRef.current = raf(cb);
      return;
    }
    if (
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function"
    ) {
      rafRef.current = window.requestAnimationFrame(cb);
      return;
    }
    // Node/test fallback — fire once on next microtask. rafRef stays null.
    queueMicrotask(() => cb(0));
  };

  const tick = () => {
    if (disposed) return;
    if (!audioCtx) return;
    const t = transportRef.current;
    if (!t) return;
    const currentTick = tickAt(audioCtx.currentTime);
    const lookaheadTicks = secondsToTick(lookaheadSec, tempos, ppq);
    const cutoff = currentTick + lookaheadTicks;
    const arr = getNotes();
    let idx = nextNoteIndexRef.current;
    while (idx < arr.length && arr[idx]!.startTicks <= cutoff) {
      const note = arr[idx]!;
      const tickOffset = note.startTicks - currentTick;
      if (tickOffset < 0) {
        idx++;
        continue;
      }
      const secondsUntilNote = tickToSeconds(tickOffset, tempos, ppq);
      const when = audioCtx.currentTime + secondsUntilNote;
      try {
        onScheduleNote?.(note, when);
      } catch {
        setState("error", positionMs, durationMs);
        stopRaf();
        transportRef.current = null;
        return;
      }
      idx++;
    }
    nextNoteIndexRef.current = idx;
    const posMs = Math.round(tickToSeconds(currentTick, tempos, ppq) * 1000);
    positionMs = posMs;
    if (idx >= arr.length && arr.length > 0) {
      setState("ended", posMs, durationMs);
      transportRef.current = null;
      pausedAtRef.current = null;
      stopRaf();
      return;
    }
    setState("playing", posMs, durationMs);
    requestRaf(tick);
  };

  const play = () => {
    if (disposed) return;
    if (!audioCtx) return;
    const arr = getNotes();
    if (arr.length === 0) return;
    if (status === "playing") return;
    void audioCtx.resume().catch(() => undefined);
    refreshDuration();
    const startTick = pausedAtRef.current
      ? pausedAtRef.current.tick
      : secondsToTick(positionMs / 1000, tempos, ppq);
    transportRef.current = {
      startCtxTime: audioCtx.currentTime,
      startTick,
    };
    pausedAtRef.current = null;
    nextNoteIndexRef.current = findFirstNoteAtOrAfter(arr, startTick);
    status = "playing";
    setState("playing", positionMs, durationMs);
    stopRaf();
    requestRaf(tick);
  };

  const pause = () => {
    if (disposed) return;
    if (!audioCtx) return;
    if (status !== "playing") return;
    const t = transportRef.current;
    if (!t) return;
    const currentTick = tickAt(audioCtx.currentTime);
    pausedAtRef.current = { tick: currentTick };
    transportRef.current = null;
    const posMs = Math.round(tickToSeconds(currentTick, tempos, ppq) * 1000);
    positionMs = posMs;
    status = "paused";
    setState("paused", posMs, durationMs);
    stopRaf();
    onStopAll?.();
  };

  const stop = () => {
    if (disposed) return;
    transportRef.current = null;
    pausedAtRef.current = null;
    nextNoteIndexRef.current = 0;
    positionMs = 0;
    status = "idle";
    refreshDuration();
    setState("idle", 0, durationMs);
    stopRaf();
    onStopAll?.();
  };

  const seek = (targetMs: number) => {
    if (disposed) return;
    if (!audioCtx) return;
    refreshDuration();
    const clamped = Math.max(0, Math.min(targetMs, durationMs));
    const newTick = secondsToTick(clamped / 1000, tempos, ppq);
    const wasPlaying = status === "playing";
    onStopAll?.();
    positionMs = clamped;
    nextNoteIndexRef.current = findFirstNoteAtOrAfter(getNotes(), newTick);
    if (wasPlaying) {
      transportRef.current = {
        startCtxTime: audioCtx.currentTime,
        startTick: newTick,
      };
      pausedAtRef.current = null;
      status = "playing";
      setState("playing", clamped, durationMs);
      stopRaf();
      requestRaf(tick);
    } else {
      transportRef.current = null;
      pausedAtRef.current = { tick: newTick };
      status = "paused";
      setState("paused", clamped, durationMs);
    }
  };

  return {
    getState: () => cachedState,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    play,
    pause,
    stop,
    seek,
    setOnScheduleNote: (cb) => {
      onScheduleNote = cb;
    },
    setOnStopAll: (cb) => {
      onStopAll = cb;
    },
    dispose: () => {
      disposed = true;
      stopRaf();
      listeners.clear();
    },
  };
};